import type { Rec, Finding } from "@shared/types.js";
import { computeStats } from "@shared/stats.js";
import { fnum, fpct } from "../lib/format.js";
import { CATS } from "../lib/comps.js";
import { DonutCard, Kpi } from "../components/Charts.js";
import { CrosscheckSummary } from "../components/CrosscheckSummary.js";
import { ExportStrip } from "../components/ExportStrip.js";

function CategoryTable({ rows, label, isNew }: { rows: Rec[]; label: string; isNew: boolean }) {
  const tot = computeStats(rows);
  const uncat = rows.filter((r) => !r.cat || !CATS.includes(r.cat as (typeof CATS)[number]));

  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th className="n">Records</th>
            {isNew ? (
              <>
                <th className="n">Received</th><th className="n">% received</th>
                <th className="n">Priced</th><th className="n">% priced</th>
                <th className="n">Awaiting</th><th className="n">Not received</th>
              </>
            ) : (
              <>
                <th className="n">Not received</th><th className="n">% not recv</th>
                <th className="n">Estimated</th><th className="n">% estimated</th>
                <th className="n">Outstanding</th><th className="n">Received</th>
              </>
            )}
            <th className="n">Crosschecked</th>
            <th className="n">Exported</th>
          </tr>
        </thead>
        <tbody>
          {CATS.map((c) => {
            const s = computeStats(rows.filter((r) => r.cat === c));
            return (
              <tr key={c}>
                <td><span className="catbadge">{c}</span></td>
                <td className="n">{fnum(s.total)}</td>
                {isNew ? (
                  <>
                    <td className="n">{fnum(s.received)}</td>
                    <td className="n">{fpct(s.received, s.total)}</td>
                    <td className="n">{fnum(s.priced)}</td>
                    <td className="n">{fpct(s.received - s.awaiting, s.received)}</td>
                    <td className="n">{fnum(s.awaiting)}</td>
                    <td className="n">{fnum(s.notReceived)}</td>
                  </>
                ) : (
                  <>
                    <td className="n">{fnum(s.notReceived)}</td>
                    <td className="n">{fpct(s.notReceived, s.total)}</td>
                    <td className="n">{fnum(s.estimated)}</td>
                    <td className="n">{fpct(s.notReceived - s.estOutstanding, s.notReceived)}</td>
                    <td className="n">{fnum(s.estOutstanding)}</td>
                    <td className="n">{fnum(s.received)}</td>
                  </>
                )}
                <td className="n">{fnum(s.crosschecked)}</td>
                <td className="n">{fnum(s.exported)}</td>
              </tr>
            );
          })}
          {uncat.length > 0 && (() => {
            const s = computeStats(uncat);
            return (
              <tr>
                <td><span className="catbadge" title="Rows with no category value">&mdash;</span></td>
                <td className="n">{fnum(s.total)}</td>
                <td className="n" colSpan={7}>no category value in the Masterfile</td>
                <td className="n">{fnum(s.crosschecked)}</td>
              </tr>
            );
          })()}
          <tr className="tfoot-row">
            <td>{label}</td>
            <td className="n">{fnum(tot.total)}</td>
            {isNew ? (
              <>
                <td className="n">{fnum(tot.received)}</td>
                <td className="n">{fpct(tot.received, tot.total)}</td>
                <td className="n">{fnum(tot.priced)}</td>
                <td className="n">{fpct(tot.received - tot.awaiting, tot.received)}</td>
                <td className="n">{fnum(tot.awaiting)}</td>
                <td className="n">{fnum(tot.notReceived)}</td>
              </>
            ) : (
              <>
                <td className="n">{fnum(tot.notReceived)}</td>
                <td className="n">{fpct(tot.notReceived, tot.total)}</td>
                <td className="n">{fnum(tot.estimated)}</td>
                <td className="n">{fpct(tot.notReceived - tot.estOutstanding, tot.notReceived)}</td>
                <td className="n">{fnum(tot.estOutstanding)}</td>
                <td className="n">{fnum(tot.received)}</td>
              </>
            )}
            <td className="n">{fnum(tot.crosschecked)}</td>
            <td className="n">{fnum(tot.exported)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function RatePanel({
  mode, rows, findings, onExport,
}: {
  mode: "new" | "est";
  rows: Rec[];
  findings: Finding[];
  onExport: (kind: "csv" | "copy") => void;
}) {
  const isNew = mode === "new";
  const acc = rows.filter((r) => r.comp === "accommodation" || r.comp === "cat_e");
  const act = rows.filter((r) => r.comp === "activity");
  const all = acc.concat(act);
  const s = computeStats(all);

  const pairs: [string, Rec[]][] = [
    ["All (Accom. + Activities)", all], ["Accommodation", acc], ["Activities", act],
  ];

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section {isNew ? "3" : "4"}</span>
          <h2><i className={`bi ${isNew ? "bi-tag" : "bi-calculator"} hico`} aria-hidden="true" />{isNew ? "New Rate 27–28" : "Estimation Rate 27–28"}</h2>
          <span className="note">
            Accommodation + Activities &middot; {fnum(all.length)} records in scope
          </span>
        </div>
        <div className="grid g-kpi">
          {isNew ? (
            <>
              <Kpi icon="bi-envelope-open" label="New rates received" value={fnum(s.received)} tone="done"
                foot={<><span className="pct">{fpct(s.received, s.total)}</span> of {fnum(s.total)} records</>} />
              <Kpi icon="bi-check2-circle" label="Pricing done (new CR input)" value={fnum(s.priced)} tone="done"
                foot={<><span className="pct">{fpct(s.received - s.awaiting, s.received)}</span> of received</>} />
              <Kpi icon="bi-hourglass-split" label="Received, not yet priced" value={fnum(s.awaiting)} tone="await"
                foot="Outstanding pricing work" />
              <Kpi icon="bi-inbox" label="Not yet received" value={fnum(s.notReceived)} tone="none"
                foot={<><span className="pct">{fpct(s.notReceived, s.total)}</span> of records</>} />
              <Kpi icon="bi-shield-check" label="Crosschecked" value={fnum(s.crosschecked)}
                foot={<><span className="pct">{fpct(s.crosschecked, s.entered)}</span> of entered records</>} />
            </>
          ) : (
            <>
              <Kpi icon="bi-inbox" label="CRs not yet received" value={fnum(s.notReceived)} tone="none"
                foot={<><span className="pct">{fpct(s.notReceived, s.total)}</span> of {fnum(s.total)} records</>} />
              <Kpi icon="bi-calculator" label="Estimations entered" value={fnum(s.estimated)} tone="est"
                foot={<><span className="pct">{fpct(s.notReceived - s.estOutstanding, s.notReceived)}</span> of not-received</>} />
              <Kpi icon="bi-hourglass-split" label="Estimation outstanding" value={fnum(s.estOutstanding)} tone="await"
                foot="Neither received nor estimated" />
              <Kpi icon="bi-envelope-open" label="CRs received (no estimate needed)" value={fnum(s.received)} tone="done"
                foot={<><span className="pct">{fpct(s.received, s.total)}</span> of records</>} />
              <Kpi icon="bi-shield-check" label="Estimation crosschecked"
                value={fnum(all.filter((r) => r.estDone && r.ccDate).length)}
                foot="Estimated records with a crosscheck date" />
            </>
          )}
        </div>
      </div>

      <div className="sec">
        <div className="sec-head">
          <h2><i className="bi bi-bar-chart-line hico" aria-hidden="true" />Status split</h2>
          <span className="note">Accommodation and Activities side by side</span>
        </div>
        <div className="card">
          <div className="grid g-donut" style={{ padding: 6 }}>
            {pairs.map(([title, set]) => {
              const st = computeStats(set);
              return isNew ? (
                <DonutCard
                  key={title} title={title}
                  subtitle={`${fnum(st.total)} ${st.total === 1 ? "record" : "records"}`}
                  centerValue={fpct(st.received, st.total, 0)} centerLabel="received"
                  segments={[
                    { label: "Priced (Done)", value: st.priced, color: "var(--s-done)" },
                    { label: "Received, awaiting pricing", value: st.awaiting, color: "var(--s-await)" },
                    { label: "Not received, not priced", value: st.total - st.priced - st.awaiting, color: "var(--s-none)" },
                  ]}
                />
              ) : (
                <DonutCard
                  key={title} title={title}
                  subtitle={`${fnum(st.notReceived)} CRs not received`}
                  centerValue={fpct(st.notReceived - st.estOutstanding, st.notReceived, 0)}
                  centerLabel="estimated"
                  segments={[
                    { label: "Estimated (Done)", value: st.estimated, color: "var(--s-est)" },
                    { label: "Outstanding", value: st.estOutstanding, color: "var(--s-none)" },
                    { label: "CR received (no estimate needed)", value: st.total - st.estimated - st.estOutstanding, color: "var(--s-done)" },
                  ]}
                />
              );
            })}
          </div>
          <div className="tblnote">
            <span className="pill p-done">Priced (new rate)</span>{" "}
            <span className="pill p-est">Estimated</span>{" "}
            <span className="pill p-await">Received, awaiting pricing</span>{" "}
            <span className="pill p-none">Outstanding / not received</span>
          </div>
        </div>
      </div>

      {([["Accommodation", acc], ["Activities", act]] as [string, Rec[]][]).map(([label, set]) => (
        <div className="sec" key={label}>
          <div className="sec-head">
            <h2><i className="bi bi-collection hico" aria-hidden="true" />{label} &mdash; by category</h2>
            <span className="note">
              Categories A&ndash;E{set.length ? "" : " · no records in scope"}
            </span>
          </div>
          <div className="card">
            <div className="grid g-donut" style={{ padding: 6 }}>
              {CATS.map((c) => {
                const st = computeStats(set.filter((r) => r.cat === c));
                return isNew ? (
                  <DonutCard
                    key={c} title={`Category ${c}`} size={132} thick={15}
                    subtitle={`${fnum(st.total)} ${st.total === 1 ? "record" : "records"}`}
                    centerValue={fpct(st.received, st.total, 0)} centerLabel="received"
                    segments={[
                      { label: "Priced", value: st.priced, color: "var(--s-done)" },
                      { label: "Awaiting pricing", value: st.awaiting, color: "var(--s-await)" },
                      { label: "Not received, not priced", value: st.total - st.priced - st.awaiting, color: "var(--s-none)" },
                    ]}
                  />
                ) : (
                  <DonutCard
                    key={c} title={`Category ${c}`} size={132} thick={15}
                    subtitle={`${fnum(st.notReceived)} not received`}
                    centerValue={fpct(st.notReceived - st.estOutstanding, st.notReceived, 0)}
                    centerLabel="estimated"
                    segments={[
                      { label: "Estimated", value: st.estimated, color: "var(--s-est)" },
                      { label: "Outstanding", value: st.estOutstanding, color: "var(--s-none)" },
                      { label: "CR received", value: st.total - st.estimated - st.estOutstanding, color: "var(--s-done)" },
                    ]}
                  />
                );
              })}
            </div>
            <CategoryTable rows={set} label={label} isNew={isNew} />
          </div>
        </div>
      ))}

      <CrosscheckSummary kind={mode} rows={all} findings={findings} />
      <ExportStrip
        name={isNew ? "New Rate" : "Estimation Rate"}
        count={all.length}
        onExport={onExport}
      />
    </>
  );
}
